import { useIntl } from 'react-intl';
import baseChart from '../../../core/components/BaseChart';
import Loader from '../../../core/components/Loader';
import { DetergentsKpiBar } from './DetergentsKpiBar';
import { useHistory } from 'react-router';
import React from 'react';
interface Props {
  detergentContainer: any;
  chartData: any;
}
export const DetergentsKpi = (props: Props): JSX.Element => {
  const [barLoaded, setBarLoaded] = React.useState<{ [key: number]: boolean }>({});
  const history = useHistory();
  const pushToDetergents = () => {
    history.push('detergents');
  };
  const intl = useIntl();
  const borderRadius = 100;
  const borderRadiusAllCorners = {
    topLeft: borderRadius,
    topRight: borderRadius,
    bottomLeft: borderRadius,
    bottomRight: borderRadius,
  };
  const lightenColor = (color: string, percent: number) => {
    if (!color) {
      return '#D3D3D3'; // grey background color
    }
    const lighten = (c: number) => Math.min(255, c + Math.floor(((255 - c) * percent) / 100));
    const r = lighten(parseInt(color.slice(1, 3), 16));
    const g = lighten(parseInt(color.slice(3, 5), 16));
    const b = lighten(parseInt(color.slice(5, 7), 16));
    return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
  };
  const detergentData = props.chartData.map((detergent: any) => {
    const currentLabel = `${detergent.label}`;
    const capacityLabel = `${detergent.label} (Capacity)`;
    const flattenedArray = props.detergentContainer.flat();
    const desiredId = detergent.id;
    const foundObject = flattenedArray.find((obj: { id: any }) => obj.id === desiredId);
    const lastUpdate = foundObject ? foundObject.latestReading : null;
    const currentDataset = {
      position: detergent.position,
      label: detergent.label,
      data: [parseFloat(detergent.currentValue ?? 0)],
      backgroundColor: detergent.detergentColor ?? '#D3D3D3',
      borderRadius: borderRadiusAllCorners,
      borderWidth: 1,
    };
    const calculateBackgroundColor = () => {
      if (detergent?.currentValue && detergent?.alarmLevel) {
        if (detergent?.currentValue < detergent?.alarmLevel) {
          return '#F7E4E5'; // red background color
        }
        return lightenColor(detergent.detergentColor, 85);
      }
      return '#D3D3D3'; // grey background color
    };
    const capacityDataset = {
      label: intl.formatMessage({ id: 'capacity' }),
      data: [parseFloat(detergent.capacity)],
      backgroundColor: calculateBackgroundColor(),
      borderRadius: borderRadiusAllCorners,
      borderWidth: 1,
    };
    const value = parseFloat(detergent.currentValue ?? 0);
    const capacity = parseFloat(detergent.capacity ?? 0);
    const percent = capacity > 0 ? Math.round((value / capacity) * 100) : 0;

    return {
      labels: [currentLabel, capacityLabel],
      datasets: [currentDataset, capacityDataset],
      percent: percent,
      lastUpdate: lastUpdate,
    };
  });
  const [renderedChart, setRenderedChart] = React.useState<JSX.Element | null>(null);

  const handleChartDraw = () => {
    if (!detergentData.length) return <Loader />;

    return (
      <div className="col-lg-12 col-md-12 col-sm-12">
        {[...detergentData]
          .sort((a, b) => (a.datasets[0].position ?? 0) - (b.datasets[0].position ?? 0))
          .map((barData: any, index: number) => {
            const { percent = '', datasets = [] } = barData;
            const label = datasets[0]?.label ?? '';
            const current = parseFloat(datasets[0]?.data?.[0] ?? 0);
            const capacity = parseFloat(datasets[1]?.data?.[0] ?? 1); // protect from divide-by-zero

            const originalIndex = props.chartData.findIndex((d: any) => d.label === label);
            const alarmLevel = parseFloat(props.chartData?.[originalIndex]?.alarmLevel ?? 0);

            const isBelowAlarm = current < alarmLevel;
            const percentageDiff = (Math.abs(current - alarmLevel) / capacity) * 100;
            const roundedPercentage = Math.round(percentageDiff);

            return (
              <div className="detergent-bar-height" key={index}>
                <div className="detergent-kpi-container">
                  <div className="detergent-bar-label">
                    {label}
                    {isBelowAlarm && label && (
                      <span className="detergent-bar-alarm">
                        {intl.formatMessage({ id: 'belowAlarm' }, { percent: roundedPercentage })}
                      </span>
                    )}
                  </div>
                  <div className="detergent-bar-percent">{label && `${percent}%`}</div>
                </div>
                <DetergentsKpiBar barData={barData} />
              </div>
            );
          })}
      </div>
    );
  };

  return (
    <div>
      <section className="container-fluid overview-chart-card">
        <div className="chart-title-section">
          <div className="chart-title">{intl.formatMessage({ id: 'detergentLevels' })}</div>
          <button
            className="btn btn-outline-secondary drs-button-shadow overview-chart-view-button"
            onClick={() => pushToDetergents()}
          >
            {intl.formatMessage({ id: 'recordLevels' })}
          </button>
          <div className="chart-sub-title">
            {intl.formatMessage({ id: 'howAreYourDetergentLevels' })}
          </div>
        </div>
        {handleChartDraw()}
      </section>
    </div>
  );
};
export default baseChart(DetergentsKpi);
